import {Checkbox} from 'platform/components';
import {match} from 'ts-pattern';

import {MouseEvent} from 'react';

import {ROW_BORDER_BOTTOM} from '../../../../../../constants/borderStyles';
import {
  SELECTION_COLUMN_WIDTH,
  SELECTION_TEST_ID_PREFIX,
} from '../../../../../../constants/selectionColumn';
import {getStickyCellBackgroundClass} from '../../../../../../utils/getStickyCellBackgroundClass';

interface RowSelectCheckboxProps {
  isSelected: boolean;
  isHighlighted?: boolean;
  /** Row ID forwarded to onToggleSelection — avoids creating an inline arrow at the call site. */
  rowId: string;
  onToggleSelection: (rowId: string) => void;
  rowIndex?: number;
  horizontalSeparators: boolean;
}

// Toggle is driven by the td's onClick rather than the Checkbox's onChange. The td
// handler stops propagation (so the row's onClick does not also toggle, which
// would cancel out) and prevents the default click action (so the input's native
// change does not fire a second toggle). The Checkbox onChange is left as a no-op
// — the visual state is fully derived from the isSelected prop.
const NOOP = () => undefined;

// The 3 px marker strip on the left edge sits above the row's inset-shadow highlight bar on this
// cell, so a highlighted row draws its red bar here, also when it is selected or hovered.
const getRowMarkerClass = (isSelected: boolean, isHighlighted: boolean) =>
  match({isSelected, isHighlighted})
    .with({isHighlighted: true}, () => 'bg-[var(--color-palettes-red-60-100)]')
    .with({isSelected: true}, () => 'bg-[var(--color-blue-400)]')
    .otherwise(() => 'bg-[var(--color-blue-400)] hidden group-hover/row:block');

export function RowSelectCheckbox(props: RowSelectCheckboxProps) {
  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    props.onToggleSelection(props.rowId);
  };

  return (
    <td
      className={`p-0 text-center select-none ${getStickyCellBackgroundClass(
        props.isSelected,
        props.isHighlighted ?? false
      )}`}
      style={{
        width: SELECTION_COLUMN_WIDTH,
        position: 'sticky',
        left: 0,
        zIndex: 'var(--z-content)',
        ...(props.horizontalSeparators ? ROW_BORDER_BOTTOM : {}),
      }}
      onClick={handleClick}
    >
      <span
        data-testid={`${SELECTION_TEST_ID_PREFIX}-marker-${props.rowIndex ?? 0}`}
        className={`absolute left-0 top-0 h-full w-[3px] pointer-events-none ${getRowMarkerClass(
          props.isSelected,
          props.isHighlighted ?? false
        )}`}
      />
      <div className="flex items-center justify-center h-full">
        <Checkbox
          value={props.isSelected}
          onChange={NOOP}
          data-testid={`${SELECTION_TEST_ID_PREFIX}-row-${props.rowIndex ?? 0}`}
        />
      </div>
    </td>
  );
}
